import React, { useState, useEffect } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Calendar,
  Clock,
  Video,
  Check,
  X,
  Sparkles,
  Heart,
  ShieldCheck,
  Users,
  Zap,
  BookOpen,
  MessageCircleHeart,
  Quote,
  ArrowRight,
  ChevronRight,
  Flame,
  Gift,
  PlayCircle,
  AlertCircle,
  Star,
} from "lucide-react";

/* ---------- Mentor & Section Images ---------- */
const MENTOR_IMAGE =
  "https://customer-assets.emergentagent.com/job_coach-connect-in/artifacts/j0pw2enx_Gangamai_LP_image.png";
const PAIN_IMAGE =
  "https://customer-assets.emergentagent.com/job_coach-connect-in/artifacts/l4jeiaoc_LP%20image.png";
const OVERWHELM_IMAGE =
  "https://images.pexels.com/photos/4474035/pexels-photo-4474035.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940";
const TRANSFORMATION_IMAGE =
  "https://images.unsplash.com/photo-1763275530166-5e265bf06450?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxoYXBweSUyMGluZGlhbiUyMGZhbWlseSUyMGNoaWxkfGVufDB8fHx8MTc3Njg1NDYyMHww&ixlib=rb-4.1.0&q=85";

/* Bonus mockup images (in order: bonus 1, 2, 3) */
const BONUS_IMAGES = [
  "https://customer-assets.emergentagent.com/job_coach-connect-in/artifacts/9avxzkn1_Bonus%201.png",
  "https://customer-assets.emergentagent.com/job_coach-connect-in/artifacts/psnv5p40_Bonus%202.png",
  "https://customer-assets.emergentagent.com/job_coach-connect-in/artifacts/kkjrpwe5_Bonus%203.png",
];

/* ---------- Primitive Styles ---------- */
const PRIMARY_BTN =
  "inline-flex items-center justify-center gap-2 px-7 py-4 text-base sm:text-lg font-semibold text-white bg-[#C05C3D] hover:bg-[#A94C30] rounded-full transition-all duration-300 shadow-[0_10px_25px_-10px_rgba(192,92,61,0.6)] hover:shadow-[0_14px_30px_-10px_rgba(192,92,61,0.75)] hover:-translate-y-0.5 active:translate-y-0";
const SECONDARY_BTN =
  "inline-flex items-center justify-center gap-2 px-7 py-4 text-base font-semibold text-stone-900 bg-white border border-stone-200 hover:border-stone-400 rounded-full transition-all duration-300";

const SECTION = "py-20 sm:py-24 lg:py-32";
const CONTAINER = "max-w-7xl mx-auto px-5 sm:px-8 lg:px-10";

/* ---------- Helper: Eyebrow Label ---------- */
const Eyebrow = ({ children, color = "text-[#A66F1F]", bg = "bg-[#E8AF63]/20" }) => (
  <span
    className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] sm:text-xs font-semibold tracking-[0.18em] uppercase ${bg} ${color}`}
  >
    <span className="w-1.5 h-1.5 rounded-full bg-[#C05C3D]" /> {children}
  </span>
);

/* =================================================================== */
/*                           TOP ATTENTION BAR                          */
/* =================================================================== */
const TopBar = () => (
  <div
    className="w-full bg-[#2A2626] text-[#F4EFE6] text-center text-[13px] sm:text-sm tracking-wide py-2.5 px-4 relative z-40"
    data-testid="top-attention-bar"
  >
    <span className="inline-flex items-center gap-2">
      <Sparkles className="w-3.5 h-3.5 text-[#E8AF63]" />
      If Parenting Is Starting to Feel More Stressful Than Joyful… This Is For You
    </span>
  </div>
);

/* =================================================================== */
/*                                 NAV                                  */
/* =================================================================== */
const Navbar = () => (
  <header
    className="sticky top-0 z-30 bg-stone-50/80 backdrop-blur-xl border-b border-stone-200/60"
    data-testid="main-navbar"
  >
    <div className={`${CONTAINER} flex items-center justify-between py-4`}>
      <a
        href="#top"
        className="flex items-center gap-2 group"
        data-testid="nav-brand"
      >
        <img alt="Gangamai — Parenting Coach" className="object-cover object-top" loading="eager" src="/7d-parenting-system/assets/logo_g.png" 
          style={{
            width: 64,
          }}
        />
        <span className="font-serif-display text-xl sm:text-2xl tracking-tight text-stone-900">
          Bright Minds
        </span>
      </a>

      <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-stone-700">
        <a href="#problem" className="hover:text-[#C05C3D] transition-colors" data-testid="nav-link-problem">The Struggle</a>
        <a href="#system" className="hover:text-[#C05C3D] transition-colors" data-testid="nav-link-system">7D System</a>
        <a href="#coach" className="hover:text-[#C05C3D] transition-colors" data-testid="nav-link-coach">About Coach</a>
        <a href="#faq" className="hover:text-[#C05C3D] transition-colors" data-testid="nav-link-faq">FAQ</a>
      </nav>

      <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} hidden sm:inline-flex !px-5 !py-2.5 !text-sm`} data-testid="nav-cta">
        Book Free Seat <ArrowRight className="w-4 h-4" />
      </a>
    </div>
  </header>
);

/* =================================================================== */
/*                                HERO                                  */
/* =================================================================== */
const Hero = () => (
  <section id="top" className={`${SECTION} pt-14 sm:pt-20 relative overflow-hidden`} data-testid="hero-section">
    {/* Decorative rings */}
    <div className="pointer-events-none absolute -top-24 -right-24 w-[520px] h-[520px] rounded-full bg-[#E8AF63]/15 blur-3xl" />
    <div className="pointer-events-none absolute bottom-0 -left-32 w-[420px] h-[420px] rounded-full bg-[#C05C3D]/10 blur-3xl" />

    <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative`}>
      {/* Text side */}
      <div className="lg:col-span-7 animate-fadeup">
        <Eyebrow>Live Free Webinar · For Busy Working Parents</Eyebrow>

        <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[68px] leading-[1.05] tracking-tight text-stone-900 mt-5">
          End the tantrums.{" "}
          <span className="italic text-[#C05C3D]">Rebuild</span> the bond.
        </h1>

        <p className="mt-6 text-lg sm:text-xl leading-relaxed text-stone-600 max-w-2xl font-body">
          A simple, personalised way to handle your child's behaviour, screen struggles and emotional bursts — calmly, without guilt, even as a busy working parent.
        </p>

        {/* Webinar meta chips */}
        <div className="mt-8 flex flex-wrap gap-3">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-full text-sm text-stone-800 shadow-sm" data-testid="hero-meta-date">
            <Calendar className="w-4 h-4 text-[#C05C3D]" />
            <span className="font-semibold">Sat, 30 May 2026</span>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-full text-sm text-stone-800 shadow-sm" data-testid="hero-meta-time">
            <Clock className="w-4 h-4 text-[#C05C3D]" />
            <span className="font-semibold">11:00 AM IST</span>
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-full text-sm text-stone-800 shadow-sm" data-testid="hero-meta-zoom">
            <Video className="w-4 h-4 text-[#C05C3D]" />
            <span className="font-semibold">Online (Zoom)</span>
          </div>
        </div>

        {/* Quick bullets */}
        <ul className="mt-8 space-y-3 text-stone-700">
          {[
            "For parents of children aged 5–15",
            "Fix tantrums, emotional bursts & screen struggles",
            "Build strong emotional bonding — without guilt",
          ].map((b) => (
            <li key={b} className="flex items-start gap-3">
              <span className="mt-1 w-5 h-5 rounded-full bg-[#C05C3D]/10 text-[#C05C3D] grid place-items-center shrink-0">
                <Check className="w-3.5 h-3.5" />
              </span>
              <span className="text-base sm:text-lg">{b}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4 items-center">
          <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={PRIMARY_BTN} data-testid="hero-cta-button">
            Book My Free Webinar Seat <ArrowRight className="w-4 h-4" />
          </a>
          <div className="flex items-center gap-2 text-sm text-stone-600">
            <span className="flex -space-x-2">
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  className="w-7 h-7 rounded-full bg-gradient-to-br from-[#E8AF63] to-[#C05C3D] border-2 border-stone-50"
                />
              ))}
            </span>
            <span>
              <b className="text-stone-900">Trusted by 100+ parents</b>
              <br />
              <span className="text-xs">Practical tools, not theory</span>
            </span>
          </div>
        </div>
      </div>

      {/* Mentor image placeholder */}
      <div className="lg:col-span-5 relative animate-fadeup" style={{ animationDelay: "120ms" }}>
        <div className="relative">
          {/* Frame backdrop */}
          <div className="absolute -inset-5 rounded-[2rem] bg-gradient-to-br from-[#E8AF63]/40 via-[#F4EFE6] to-[#C05C3D]/20 blur-sm" />
          <div className="relative rounded-[2rem] overflow-hidden border border-stone-200 shadow-2xl shadow-stone-900/10 bg-stone-100" data-testid="hero-mentor-image">
            <img
              src={MENTOR_IMAGE}
              alt="Gangamai — Parenting Coach"
              className="w-full object-cover object-top"
              // className="w-full h-[520px] sm:h-[600px] object-cover object-top"
              loading="eager"
            />
            {/* Caption card */}
            <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-xl rounded-2xl p-4 sm:p-5 border border-stone-200 shadow-lg flex items-center gap-4">
              <div className="w-11 h-11 rounded-full bg-[#C05C3D] text-white grid place-items-center font-serif-display text-lg">
                G
              </div>
              <div>
                <div className="font-serif-display text-lg text-stone-900 leading-tight">Gangamai</div>
                <div className="text-xs text-stone-600">Parenting Coach · Creator of the 7D System</div>
              </div>
            </div>
          </div>

          {/* Floating pill */}
          <div className="absolute -top-4 -right-4 sm:-right-8 bg-stone-900 text-white px-4 py-3 rounded-2xl shadow-xl animate-float-slow hidden sm:block">
            <div className="text-[10px] uppercase tracking-widest text-[#E8AF63]">Webinar</div>
            <div className="text-sm font-semibold">Seats filling fast</div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* =================================================================== */
/*                       TRUST TICKER (MARQUEE)                         */
/* =================================================================== */
const TrustTicker = () => {
  const items = [
    "100+ parents transformed",
    "Practical tools, not theory",
    "Step-by-step, personalised system",
    "Rooted in Indian parenting reality",
    "Calm home, connected child",
    "For working mothers & fathers",
  ];
  const row = [...items, ...items];
  return (
    <div className="border-y border-stone-200 bg-[#F4EFE6] overflow-hidden" data-testid="trust-ticker">
      <div className="relative py-5">
        <div className="flex gap-12 whitespace-nowrap animate-marquee-7">
          {row.map((t, i) => (
            <span key={i} className="flex items-center gap-3 text-stone-600 text-sm tracking-wide font-medium">
              <Star className="w-3.5 h-3.5 text-[#C05C3D]" /> {t}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

/* =================================================================== */
/*                    SECTION 2 — IDENTIFICATION                        */
/* =================================================================== */
const Identification = () => {
  const items = [
    "You feel guilty even after trying your best",
    "You lose patience and regret it later",
    "You feel disconnected from your child",
    "You're constantly searching for \"what will actually work\"",
  ];
  return (
    <section id="problem" className={`${SECTION} bg-stone-50`} data-testid="identification-section">
      <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center`}>
        <div className="lg:col-span-5 order-2 lg:order-1 relative">
          <div className="relative rounded-[2rem] overflow-hidden border border-stone-200 shadow-xl">
            <img
              src={OVERWHELM_IMAGE}
              alt="An exhausted Indian parent trying to work as children play around"
              className="w-full h-[420px] sm:h-[520px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 via-stone-900/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 bg-white/95 backdrop-blur-xl rounded-2xl p-4 border border-stone-200 flex items-center gap-3">
              <MessageCircleHeart className="w-5 h-5 text-[#C05C3D]" />
              <span className="text-sm text-stone-700">
                “I love my child — but some days, I just don't have anything left to give.”
              </span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <Eyebrow>You are not alone</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5 text-stone-900">
            You're not alone if parenting has started to feel <span className="italic text-[#C05C3D]">overwhelming…</span>
          </h2>
          <ul className="mt-8 space-y-4">
            {items.map((t, i) => (
              <li
                key={i}
                className="flex items-start gap-4 p-5 bg-white rounded-2xl border border-stone-200 shadow-sm hover:shadow-md transition-shadow"
                data-testid={`identification-item-${i}`}
              >
                <span className="mt-1 w-6 h-6 rounded-full bg-[#E8AF63]/25 text-[#A66F1F] grid place-items-center shrink-0">
                  <AlertCircle className="w-4 h-4" />
                </span>
                <span className="text-base sm:text-lg text-stone-800 leading-relaxed">{t}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8 text-center lg:text-left">
            <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN}`} data-testid="identification-cta">
              This is exactly what I'm facing <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                         SECTION 3 — PAIN                             */
/* =================================================================== */
const Pain = () => {
  const items = [
    { icon: Flame, t: "Constant arguments" },
    { icon: Heart, t: "Emotional exhaustion" },
    { icon: ShieldCheck, t: "Parenting guilt" },
    { icon: AlertCircle, t: "Feeling like you're failing despite trying" },
  ];
  return (
    <section className={`${SECTION} bg-[#2A2626] text-[#F4EFE6] relative overflow-hidden grain-bg`} data-testid="pain-section">
      <div className={`${CONTAINER} relative`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Text side */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Eyebrow bg="bg-[#E8AF63]/15" color="text-[#E8AF63]">The Real Problem</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5">
              The real problem isn't behaviour… <br />
              <span className="italic text-[#E8AF63]">It's the growing distance</span> between you and your child.
            </h2>
            <p className="mt-6 text-lg sm:text-xl text-[#F4EFE6]/80 leading-relaxed max-w-2xl">
              You're not just tired… You're emotionally drained from repeating the same cycle every day.
            </p>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {items.map(({ icon: Icon, t }, i) => (
                <div
                  key={i}
                  className="p-5 rounded-2xl bg-white/5 border border-white/10 backdrop-blur-xl hover:bg-white/10 transition flex items-center gap-4"
                  data-testid={`pain-card-${i}`}
                >
                  <div className="w-10 h-10 rounded-xl bg-[#E8AF63]/15 text-[#E8AF63] grid place-items-center shrink-0">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-base sm:text-lg font-medium">{t}</div>
                </div>
              ))}
            </div>

            <div className="mt-10 max-w-2xl border-l-2 border-[#E8AF63] pl-6">
              <p className="font-serif-display italic text-2xl sm:text-3xl leading-snug">
                And deep down… you know something needs to change.
              </p>
            </div>
            <div className="text-center lg:text-left">
              <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} mt-10`} data-testid="pain-cta">
                I Want to Fix This — Register Now <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Image side */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[#E8AF63]/30 to-[#C05C3D]/20 blur-sm" />
              <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl bg-[#1f1c1c]">
                <img
                  src={PAIN_IMAGE}
                  alt="A tired Indian mother working on her laptop while her son is absorbed in his tablet — the everyday distance"
                  className="w-full h-auto object-contain"
                />
                <div className="relative sm:absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#2A2626]/90 to-transparent">
                  <div className="bg-[#2A2626]/85 backdrop-blur rounded-2xl p-4 border border-white/10 flex items-start gap-3">
                    <MessageCircleHeart className="w-5 h-5 text-[#E8AF63] shrink-0 mt-0.5" />
                    <span className="text-sm text-[#F4EFE6]/90 leading-relaxed">
                      Same room. Same sofa. Two different worlds.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                SECTION 4 — BREAK FALSE SOLUTIONS                     */
/* =================================================================== */
const BreakFalseSolutions = () => {
  const tried = [
    "Random parenting tips",
    "YouTube videos",
    "Advice from others",
    "Trial and error",
  ];
  return (
    <section className={`${SECTION} bg-[#F4EFE6]`} data-testid="false-solutions-section">
      <div className={`${CONTAINER}`}>
        <div className="text-center max-w-3xl mx-auto">
          <Eyebrow>Why it isn't working</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5 text-stone-900">
            Why most parenting advice is <span className="italic text-[#C05C3D]">not working</span> for you
          </h2>
          <p className="mt-5 text-stone-600 text-lg">You've probably already tried…</p>
        </div>

        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          {tried.map((item, i) => (
            <div
              key={i}
              className="relative p-5 rounded-2xl bg-white border border-stone-200 text-center group"
              data-testid={`tried-item-${i}`}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-red-50 text-red-500 grid place-items-center border border-red-100">
                <X className="w-4 h-4" />
              </div>
              <div className="mt-2 text-stone-700 text-sm sm:text-base font-medium">{item}</div>
            </div>
          ))}
        </div>

        <div className="mt-14 max-w-3xl mx-auto rounded-[2rem] bg-white border border-stone-200 p-8 sm:p-12 text-center shadow-sm">
          <div className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.18em] uppercase text-[#C05C3D]">
            <Zap className="w-3.5 h-3.5" /> The Core Shift
          </div>
          <p className="mt-4 font-serif-display text-2xl sm:text-3xl lg:text-4xl leading-snug text-stone-900">
            Information is not your problem. <br />
            <span className="italic text-[#C05C3D]">Implementation in YOUR situation</span> is.
          </p>
          <p className="mt-6 text-stone-600 text-lg">
            Your child is unique. Your parenting approach should be too.
          </p>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                SECTION 5 — UNIQUE MECHANISM (7D)                     */
/* =================================================================== */
const UniqueMechanism = () => {
  const pillars = [
    { icon: Heart, title: "Emotional Wellbeing", desc: "Understand what your child is really feeling beneath the behaviour." },
    { icon: Users, title: "Behaviour Patterns", desc: "Spot, shift and reset recurring triggers — without conflict." },
    { icon: ShieldCheck, title: "Confidence & Boundaries", desc: "Loving limits that build respect, not rebellion." },
    { icon: MessageCircleHeart, title: "Parent–Child Bonding", desc: "Small daily rituals that rebuild trust and conversation." },
    { icon: Sparkles, title: "Holistic Development", desc: "Academics, empathy, resilience — developing the whole child." },
  ];

  return (
    <section id="system" className={`${SECTION} bg-stone-50 relative`} data-testid="unique-mechanism-section">
      <div className={`${CONTAINER}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-14">
          <div className="lg:col-span-8">
            <Eyebrow>The 7D Parenting System</Eyebrow>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.08] mt-5 text-stone-900">
              A simple way to fix behaviour — <br className="hidden sm:block" />
              <span className="italic text-[#C05C3D]">without overwhelm.</span>
            </h2>
          </div>
          <div className="lg:col-span-4">
            <p className="text-stone-700 text-lg leading-relaxed">
              This is not theory. It's a step-by-step way to handle real-life parenting situations — based on your child,
              your environment, and your daily reality.
            </p>
          </div>
        </div>

        {/* Tetris-style bento */}
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 sm:gap-5">
          {pillars.map(({ icon: Icon, title, desc }, i) => {
            // vary sizes for visual rhythm
            const span =
              i === 0
                ? "md:col-span-3"
                : i === 1
                ? "md:col-span-3"
                : i === 2
                ? "md:col-span-2"
                : i === 3
                ? "md:col-span-2"
                : "md:col-span-2";
            return (
              <div
                key={i}
                className={`${span} bg-white rounded-2xl p-7 sm:p-8 border border-stone-200 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all`}
                data-testid={`pillar-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-[#C05C3D]/10 text-[#C05C3D] grid place-items-center">
                  <Icon className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-serif-display text-xl sm:text-2xl text-stone-900">{title}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed">{desc}</p>
              </div>
            );
          })}
          {/* Key line card */}
          <div className="md:col-span-6 bg-stone-900 text-[#F4EFE6] rounded-2xl p-8 sm:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-xl bg-[#E8AF63]/20 text-[#E8AF63] grid place-items-center shrink-0">
                <Zap className="w-5 h-5" />
              </div>
              <p className="font-serif-display text-xl sm:text-2xl lg:text-3xl leading-snug max-w-2xl">
                We don't overwhelm you. We help you fix <span className="italic text-[#E8AF63]">ONE thing at a time.</span>
              </p>
            </div>
            <div className="w-full text-center lg:text-left">
              <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} shrink-0`} data-testid="mechanism-cta">
                Show Me How It Works <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                   SECTION 6 — WEBINAR CONTENT                        */
/* =================================================================== */
const WebinarContent = () => {
  const items = [
    "A simple way to handle tantrums calmly",
    "A clear method to reduce screen dependency",
    "A practical way to rebuild emotional bonding",
    "A system to stay calm even during triggers",
    "A better understanding of your child's behaviour",
  ];
  return (
    <section className={`${SECTION} bg-[#F4EFE6]`} data-testid="webinar-content-section">
      <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start`}>
        <div className="lg:col-span-5">
          <Eyebrow>Inside The Free Webinar</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5 text-stone-900">
            In this FREE webinar, you'll walk away with…
          </h2>
          <div className="mt-8 p-6 rounded-2xl bg-gradient-to-br from-[#E8AF63]/25 to-[#C05C3D]/10 border border-[#E8AF63]/40">
            <div className="flex items-center gap-3">
              <Gift className="w-5 h-5 text-[#C05C3D]" />
              <div className="text-sm font-semibold text-stone-900 uppercase tracking-wider">Bonus Tease</div>
            </div>
            <p className="mt-2 text-stone-800 text-lg leading-relaxed">
              Real-life parenting scripts you can use <i>immediately</i> — not next month.
            </p>
          </div>
          <div className="text-center lg:text-left">
            <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} mt-8`} data-testid="webinar-content-cta">
              Join the Free Webinar <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {items.map((t, i) => (
            <div
              key={i}
              className="group relative p-6 rounded-2xl bg-white border border-stone-200 hover:border-[#C05C3D]/40 transition-all overflow-hidden"
              data-testid={`webinar-item-${i}`}
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#C05C3D] text-white grid place-items-center font-serif-display text-lg shrink-0">
                  {i + 1}
                </div>
                <div className="text-stone-800 text-base sm:text-lg font-medium leading-relaxed">{t}</div>
              </div>
              <div className="absolute -bottom-8 -right-8 w-24 h-24 rounded-full bg-[#E8AF63]/15 group-hover:bg-[#E8AF63]/25 transition" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                   SECTION 7 — TRANSFORMATION                         */
/* =================================================================== */
const Transformation = () => {
  const pairs = [
    { before: "Reactive, stressed parent", after: "Calm, present parent" },
    { before: "Child avoids you", after: "Child opens up to you" },
    { before: "Daily conflicts", after: "Healthy communication" },
  ];
  return (
    <section className={`${SECTION} relative overflow-hidden`} data-testid="transformation-section">
      <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center`}>
        <div className="lg:col-span-6">
          <Eyebrow>Your Future Identity</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5 text-stone-900">
            Imagine ending your day feeling <span className="italic text-[#C05C3D]">happy…</span>
            <br /> not guilty.
          </h2>
          <p className="mt-5 text-lg text-stone-600 leading-relaxed max-w-xl">
            A different home is possible — where your child runs <i>to</i> you, not away from you.
          </p>

          <div className="mt-8 space-y-4">
            {pairs.map(({ before, after }, i) => (
              <div
                key={i}
                className="flex flex-col sm:flex-row items-stretch gap-3 rounded-2xl overflow-hidden border border-stone-200 bg-white shadow-sm"
                data-testid={`transformation-pair-${i}`}
              >
                <div className="flex-1 p-5 bg-stone-50 flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-red-50 text-red-500 grid place-items-center shrink-0">
                    <X className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-stone-500 line-through">{before}</span>
                </div>
                <div className="hidden sm:flex items-center px-2 text-stone-400">
                  <ChevronRight className="w-4 h-4" />
                </div>
                <div className="flex-1 p-5 flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-[#C05C3D]/10 text-[#C05C3D] grid place-items-center shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </span>
                  <span className="text-stone-900 font-semibold">{after}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center lg:text-left">
            <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} mt-10`} data-testid="transformation-cta">
              I Want This Transformation <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-6 relative">
          <div className="absolute -inset-4 rounded-[2.5rem] bg-gradient-to-br from-[#E8AF63]/40 to-[#C05C3D]/20 blur-sm" />
          <div className="relative rounded-[2.5rem] overflow-hidden border border-stone-200 shadow-2xl">
            <img
              src={TRANSFORMATION_IMAGE}
              alt="Happy Indian family — parent and child connected, at peace"
              className="w-full h-[500px] sm:h-[620px] object-cover"
            />
            <div className="relative sm:absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-stone-900/70 to-transparent">
              <div className="bg-white/95 backdrop-blur rounded-2xl p-4 border border-white/60">
                <div className="text-xs tracking-[0.18em] uppercase text-[#C05C3D] font-semibold">After</div>
                <div className="text-stone-900 font-serif-display text-lg leading-tight mt-1">
                  A calmer home. A child who feels safe with you.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                   SECTION 8 — FOUNDER STORY                          */
/* =================================================================== */
const FounderStory = () => (
  <section id="coach" className={`${SECTION} bg-[#F4EFE6] relative`} data-testid="founder-section">
    <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center`}>
      <div className="lg:col-span-5 relative">
        <div className="relative rounded-[2rem] overflow-hidden border border-stone-200 shadow-2xl" data-testid="founder-mentor-image">
          <img
            src={MENTOR_IMAGE}
            alt="Gangamai — Parenting Coach"
            className="w-full object-cover object-top"
          />
        </div>
        <div className="absolute -bottom-6 -right-4 sm:-right-10 bg-white px-5 py-4 rounded-2xl border border-stone-200 shadow-xl max-w-[240px]">
          <Quote className="w-5 h-5 text-[#C05C3D]" />
          <p className="mt-2 text-sm text-stone-700 leading-relaxed italic">
            Parenting is not difficult… parenting <b>without the right system</b> is.
          </p>
        </div>
      </div>

      <div className="lg:col-span-7">
        <Eyebrow>Meet Your Coach</Eyebrow>
        <h2 className="font-serif-display text-2xl sm:text-3xl lg:text-4xl leading-[1.1] mt-5 text-stone-900">
          Gangamai — a parenting coach who doesn't teach theory…
          <br /> <span className="italic text-[#C05C3D]">but helps you create real-life transformation at home.</span>
        </h2>

        <div className="mt-8 space-y-5 text-stone-700 text-base sm:text-lg leading-relaxed font-body">
          <p>
            Gangamai grew up in a simple village environment where parenting was intentional — her parents focused on
            emotional, mental, and overall wellbeing. She saw first-hand what <b>conscious parenting</b> can do.
          </p>
          <p>
            Then she became a working mother herself — and saw a very different reality. Parents overwhelmed. Children
            disconnected. Well-meaning families, unsure of what was going wrong.
          </p>
          <p>
            That's when she realised: <i>Parenting is not difficult. Parenting without the right system is.</i>
          </p>
          <p>
            Over the years, she discovered how to enjoy parenthood, handle real-life challenges, and give children the
            right experiences. And that's how the <b>7D Parenting System</b> was created — to help parents create that
            same clarity, connection and confidence in their homes.
          </p>
        </div>
        <div className="text-center lg:text-left">
          <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} mt-10`} data-testid="founder-cta">
            Learn This System Now <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  </section>
);

/* =================================================================== */
/*                   SECTION 9 — SOCIAL PROOF                           */
/* =================================================================== */
const SocialProof = () => {
  const items = [
    {
      before: "Child was introverted, barely spoke at home",
      after: "Now expressive, confident and shares openly",
      name: "Priya S.",
      city: "Bengaluru",
    },
    {
      before: "Parent anxious, constantly losing patience",
      after: "Calm, confident and finally enjoying parenting",
      name: "Arjun K.",
      city: "Pune",
    },
    {
      before: "Daily screen-time battles & tantrums",
      after: "Peaceful evenings, healthy screen boundaries",
      name: "Meena R.",
      city: "Hyderabad",
    },
  ];
  return (
    <section className={`${SECTION} bg-stone-50`} data-testid="social-proof-section">
      <div className={`${CONTAINER}`}>
        <div className="max-w-2xl">
          <Eyebrow>Real Parents. Real Change.</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5 text-stone-900">
            What other parents are <span className="italic text-[#C05C3D]">experiencing</span>
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {items.map((t, i) => (
            <div
              key={i}
              className="bg-white rounded-2xl border border-stone-200 p-7 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center gap-1 text-[#E8AF63]">
                {[0, 1, 2, 3, 4].map((s) => (
                  <Star key={s} className="w-4 h-4 fill-[#E8AF63]" />
                ))}
              </div>
              <div className="mt-5 p-4 rounded-xl bg-stone-50 border border-stone-100">
                <div className="text-xs font-semibold tracking-wider uppercase text-stone-500 mb-1">Before</div>
                <div className="text-stone-700">{t.before}</div>
              </div>
              <div className="mt-3 p-4 rounded-xl bg-[#C05C3D]/5 border border-[#C05C3D]/15">
                <div className="text-xs font-semibold tracking-wider uppercase text-[#C05C3D] mb-1">After</div>
                <div className="text-stone-900 font-semibold">{t.after}</div>
              </div>
              <div className="mt-6 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#E8AF63] to-[#C05C3D]" />
                <div>
                  <div className="text-sm font-semibold text-stone-900">{t.name}</div>
                  <div className="text-xs text-stone-500">{t.city}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={PRIMARY_BTN} data-testid="social-proof-cta">
            I Want Similar Results <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                   SECTION 10 — BONUS STACK                           */
/* =================================================================== */
const BonusStack = () => {
  const bonuses = [
    { icon: BookOpen, title: "Parenting Worksheets", desc: "Printable daily reflection & tracking sheets to keep you consistent." },
    { icon: MessageCircleHeart, title: "Daily Parent Scripts", desc: "Exact words to use in tough moments — tantrums, screen fights, bedtime." },
    { icon: Users, title: "Expert Sessions", desc: "Specialised guest sessions covering anxiety, academics & teen years." },
  ];
  return (
    <section className={`${SECTION} bg-[#F4EFE6]`} data-testid="bonus-section">
      <div className={`${CONTAINER}`}>
        <div className="max-w-2xl">
          <Eyebrow>Join & Get Free Bonuses</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5 text-stone-900">
            When you join, you also get…
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {bonuses.map(({ icon: Icon, title, desc }, i) => (
            <div
              key={i}
              className="relative bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden flex flex-col"
              data-testid={`bonus-${i}`}
            >
              {/* Bonus mockup image */}
              <div className="relative aspect-[4/3] w-full bg-[#F4EFE6] overflow-hidden">
                <img
                  src={BONUS_IMAGES[i]}
                  alt={`${title} — bonus mockup`}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-[#C05C3D] text-white text-[10px] font-bold tracking-wider uppercase shadow">
                  Bonus {i + 1}
                </div>
              </div>
              <div className="p-7">
                <div className="w-12 h-12 rounded-xl bg-[#E8AF63]/25 text-[#A66F1F] grid place-items-center">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="mt-5 font-serif-display text-xl sm:text-2xl text-stone-900">{title}</h3>
                <p className="mt-2 text-stone-600 leading-relaxed">{desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 max-w-3xl mx-auto text-center">
          <p className="text-stone-700 text-lg italic">
            Everything you need to start seeing change <b>immediately.</b>
          </p>
          <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} mt-6`} data-testid="bonus-cta">
            Claim My Free Spot + Bonuses <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                   SECTION 11 — URGENCY                               */
/* =================================================================== */
const Urgency = () => {
  const points = [
    { icon: Users, t: "Limited seats" },
    { icon: PlayCircle, t: "Live session (not recorded)" },
    { icon: Flame, t: "Early intervention matters" },
  ];
  return (
    <section className={`${SECTION} bg-stone-900 text-stone-100 relative overflow-hidden`} data-testid="urgency-section">
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#C05C3D]/20 blur-3xl" />
      <div className={`${CONTAINER} relative`}>
        <div className="max-w-3xl">
          <Eyebrow bg="bg-[#E8AF63]/15" color="text-[#E8AF63]">Why Parents Regret Waiting</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5 text-white">
            Behaviour patterns don't disappear with time.
            <br /> <span className="italic text-[#E8AF63]">They become stronger.</span>
          </h2>
        </div>

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {points.map(({ icon: Icon, t }, i) => (
            <div
              key={i}
              className="p-6 rounded-2xl bg-white/5 border border-white/10 backdrop-blur flex items-center gap-4"
              data-testid={`urgency-item-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#E8AF63]/20 text-[#E8AF63] grid place-items-center shrink-0">
                <Icon className="w-5 h-5" />
              </div>
              <div className="text-lg font-semibold">{t}</div>
            </div>
          ))}
        </div>
        <div className="text-center lg:text-left">
          <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} mt-10`} data-testid="urgency-cta">
            Reserve My Seat Before It's Full <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                   SECTION 12 — FAQ                                   */
/* =================================================================== */
const FAQ = () => {
  const faqs = [
    { q: "Is this only for mothers?", a: "Not at all. Fathers, primary caregivers and even grandparents actively involved in parenting will benefit. The 7D system works regardless of who the primary caretaker is." },
    { q: "What if my child is very stubborn?", a: "Stubbornness is usually unmet emotion in disguise. In the webinar you'll learn how to read what's behind the behaviour — and respond in a way that shifts the pattern calmly." },
    { q: "Will I get practical tools?", a: "Yes — real scripts, daily micro-routines and tracking worksheets you can use the same evening. No fluff, no theory." },
    { q: "I've tried other programs — how is this different?", a: "Most programs give you information. The 7D System is built around implementation in YOUR unique situation — child, home, schedule — so change actually happens." },
    { q: "What if I miss the session?", a: "The webinar is live so you get maximum impact. Registered parents get a short summary note, but the full experience (including Q&A) is live only." },
    { q: "I don't have much time. Will this still work?", a: "Yes. The system is designed for busy working parents. Small, specific shifts — not hours of new activities." },
    { q: "What if my child doesn't listen at all?", a: "That's often the loudest signal that connection has to come before correction. You'll learn exactly how to rebuild that in the webinar." },
  ];
  return (
    <section id="faq" className={`${SECTION} bg-stone-50`} data-testid="faq-section">
      <div className={`${CONTAINER} grid grid-cols-1 lg:grid-cols-12 gap-12`}>
        <div className="lg:col-span-5">
          <Eyebrow>Questions Parents Ask</Eyebrow>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.1] mt-5 text-stone-900">
            Still <span className="italic text-[#C05C3D]">wondering?</span> <br />Let's answer it.
          </h2>
          <p className="mt-5 text-stone-600 text-lg max-w-md">
            The most common questions we get from working parents — answered honestly.
          </p>
          <div className="text-center lg:text-left">
            <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} mt-8`} data-testid="faq-cta">
              Still thinking? Save your seat now <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <Accordion type="single" collapsible className="w-full space-y-3" data-testid="faq-accordion">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                className="bg-white rounded-2xl border border-stone-200 px-5 !border-b"
                data-testid={`faq-item-${i}`}
              >
                <AccordionTrigger className="py-5 text-left text-base sm:text-lg font-semibold text-stone-900 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-stone-600 text-base leading-relaxed pb-5 pt-0">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

/* =================================================================== */
/*                   SECTION 13 — FINAL CLOSE                           */
/* =================================================================== */
const FinalClose = () => (
  <section
    id="register"
    className={`${SECTION} bg-gradient-to-br from-[#F4EFE6] via-stone-50 to-[#E8AF63]/20 relative overflow-hidden`}
    data-testid="final-close-section"
  >
    <div className="absolute -top-32 -left-32 w-[500px] h-[500px] rounded-full bg-[#C05C3D]/10 blur-3xl" />
    <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#E8AF63]/20 blur-3xl" />
    <div className={`${CONTAINER} relative`}>
      <div className="max-w-4xl mx-auto text-center">
        <Eyebrow>The Choice Is Yours</Eyebrow>
        <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] mt-6 text-stone-900">
          If nothing changes… <span className="italic text-[#C05C3D]">nothing changes.</span>
        </h2>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
          <div className="p-6 rounded-2xl bg-white border border-stone-200 text-left">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-red-500">
              <X className="w-4 h-4" /> Option A
            </div>
            <p className="mt-2 text-stone-700 text-lg">Continue feeling stuck, guilty and overwhelmed — hoping things improve on their own.</p>
          </div>
          <div className="p-6 rounded-2xl bg-stone-900 text-stone-100 text-left">
            <div className="inline-flex items-center gap-2 text-sm font-semibold text-[#E8AF63]">
              <Check className="w-4 h-4" /> Option B
            </div>
            <p className="mt-2 text-lg">Learn a simple, personalised system that actually works for your child — and start tonight.</p>
          </div>
        </div>

        <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} mt-10 !text-lg`} data-testid="final-cta">
          Register for the Webinar <ArrowRight className="w-5 h-5" />
        </a>
        <p className="mt-4 text-sm text-stone-600">Limited live seats · No recording · Real tools you can use tonight.</p>
      </div>
    </div>
  </section>
);

/* =================================================================== */
/*                           STICKY CTA BAR                             */
/* =================================================================== */
const useCountdown = (hours = 2, minutes = 14, seconds = 36) => {
  const [t, setT] = useState(hours * 3600 + minutes * 60 + seconds);
  useEffect(() => {
    const id = setInterval(() => setT((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(id);
  }, []);
  const hh = String(Math.floor(t / 3600)).padStart(2, "0");
  const mm = String(Math.floor((t % 3600) / 60)).padStart(2, "0");
  const ss = String(t % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const StickyCTA = () => {
  const timer = useCountdown();
  return (
    <div
      className="fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-stone-200 shadow-[0_-10px_30px_-15px_rgba(0,0,0,0.15)]"
      data-testid="sticky-cta-bar"
    >
      <div className={`${CONTAINER} flex flex-col sm:flex-row items-center justify-between gap-3 py-3`}>
        <div className="hidden sm:flex items-center gap-3 text-sm">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 font-semibold text-xs">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE
          </span>
          <span className="text-stone-700 font-medium">Seats filling fast</span>
          <span className="hidden sm:inline text-stone-400">·</span>
          <span className="hidden sm:inline text-stone-700">
            Registration closes in{" "}
            <span className="font-mono font-semibold text-stone-900" data-testid="sticky-timer">
              {timer}
            </span>
          </span>
        </div>
        <div className="flex flex-col sm:hidden items-center gap-3 text-sm">
          {/* <div>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-50 text-red-600 font-semibold text-xs">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> LIVE
            </span>
            <span className="text-stone-700 font-medium">Seats filling fast</span>
          </div> */}
          <span className="inline text-stone-700 text-base">
            Registration closes in{" "}
            <span className="font-mono font-semibold text-stone-900" data-testid="sticky-timer">
              {timer}
            </span>
          </span>
        </div>
        <a href="https://www.brightminds.world/sessions/Parenting-69ef392a43eb54710a30e3d0" className={`${PRIMARY_BTN} !px-6 !py-3 !text-sm`} data-testid="sticky-cta-button">
          Register Free Webinar <ArrowRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

/* =================================================================== */
/*                               FOOTER                                 */
/* =================================================================== */
const Footer = () => (
  <footer className="bg-stone-900 text-stone-300 pt-16 pb-28" data-testid="footer">
    <div className={`${CONTAINER} grid grid-cols-1 md:grid-cols-3 gap-10`}>
      <div>
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-full bg-[#C05C3D] text-white grid place-items-center font-serif-display text-lg">
            G
          </span>
          <span className="font-serif-display text-2xl text-white">Gangamai</span>
        </div>
        <p className="mt-4 text-sm text-stone-400 leading-relaxed max-w-xs">
          A parenting coach helping Indian families create clarity, connection and confidence at home — one calm
          evening at a time.
        </p>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Explore</div>
        <ul className="mt-4 space-y-2 text-sm">
          <li><a href="#problem" className="hover:text-white">The Struggle</a></li>
          <li><a href="#system" className="hover:text-white">7D Parenting System</a></li>
          <li><a href="#coach" className="hover:text-white">About Gangamai</a></li>
          <li><a href="#faq" className="hover:text-white">FAQ</a></li>
        </ul>
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Free Webinar</div>
        <ul className="mt-4 space-y-2 text-sm">
          <li className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[#E8AF63]" /> Sat, 30 May 2026</li>
          <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#E8AF63]" /> 11:00 AM IST</li>
          <li className="flex items-center gap-2"><Video className="w-4 h-4 text-[#E8AF63]" /> Online (Zoom)</li>
        </ul>
      </div>
    </div>
    <div className={`${CONTAINER} mt-12 pt-6 border-t border-stone-800 flex flex-col sm:flex-row justify-between gap-3 text-xs text-stone-500`}>
      <div>© {new Date().getFullYear()} Gangamai Parenting. All rights reserved.</div>
      <div className="flex gap-6">
        <a href="#" className="hover:text-stone-300">Privacy</a>
        <a href="#" className="hover:text-stone-300">Terms</a>
        <a href="#" className="hover:text-stone-300">Contact</a>
      </div>
    </div>
  </footer>
);

/* =================================================================== */
/*                              PAGE                                    */
/* =================================================================== */
export default function GangamaiLanding() {
  return (
    <div className="min-h-screen bg-stone-50 text-stone-900 font-body">
      <TopBar />
      <Navbar />
      <Hero />
      <TrustTicker />
      <Identification />
      <Pain />
      <BreakFalseSolutions />
      <UniqueMechanism />
      <WebinarContent />
      <Transformation />
      <FounderStory />
      <SocialProof />
      <BonusStack />
      <Urgency />
      <FAQ />
      <FinalClose />
      <Footer />
      <StickyCTA />
    </div>
  );
}
